"use client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useWorkspaceNavigation } from "@/hooks/use-workspace-navigation";
import { signOut } from "@/lib/auth/utils";
import { routes } from "@/lib/navigation/routes";
import { useWorkspace } from "@/providers/workspace-provider";
import { useQueryClient } from "@tanstack/react-query";
import { IconLaptop, IconMoon, IconSun, IconUser } from "@teral/icons";
import { cn } from "cn";
import { useTheme } from "next-themes";
import Link from "next/link";

const THEMES = [
  { value: "system", label: "System", icon: IconLaptop },
  { value: "light", label: "Light", icon: IconSun },
  { value: "dark", label: "Dark", icon: IconMoon },
] as const;

export function UserButton({ variant = "inline" }: { variant?: "inline" | "rail" }) {
  const rail = variant === "rail";
  const { user } = useWorkspace();
  const workspace = useWorkspaceNavigation();
  const { theme, setTheme } = useTheme();
  const queryClient = useQueryClient();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Account menu"
        className={cn(
          "group/user flex shrink-0 cursor-pointer items-center outline-hidden focus-visible:outline-2 focus-visible:outline-(--focus-ring)",
          rail
            ? "min-w-0 flex-1 gap-2.5 rounded-md p-1 pr-2 text-left transition-colors hover:bg-grayA-3 data-[popup-open]:bg-grayA-3 group-data-[collapsible=icon]:flex-none group-data-[collapsible=icon]:pr-1"
            : "rounded-full",
        )}
      >
        <Avatar className="size-7 rounded-full shadow-[0_0_0_1px_var(--color-grayA-5)] transition-shadow group-hover/user:shadow-[0_0_0_1px_var(--color-grayA-5),0_0_0_3px_var(--color-grayA-3)] group-data-[popup-open]/user:shadow-[0_0_0_1px_var(--color-accent-8),0_0_0_3px_var(--color-accentA-4)]">
          {user?.avatarUrl && <AvatarImage src={user.avatarUrl} alt="Profile picture" />}
          <AvatarFallback name={user?.email ?? "Username"} />
        </Avatar>
        {rail ? (
          <span className="secret min-w-0 flex-1 truncate text-xs font-medium text-gray-11 group-data-[collapsible=icon]:hidden">
            {user?.email ?? "Account"}
          </span>
        ) : null}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side={rail ? "top" : "bottom"}
        align={rail ? "start" : "end"}
        className="w-56 p-0"
      >
        {user?.email && (
          <DropdownMenuGroup className="border-b px-2 py-2">
            <DropdownMenuLabel
              title={user.email}
              className="secret block truncate px-0 py-0 text-sm text-gray-12"
            >
              {user.email}
            </DropdownMenuLabel>
          </DropdownMenuGroup>
        )}
        <DropdownMenuGroup className="p-1">
          <DropdownMenuItem
            className="h-8 cursor-pointer gap-2 px-2 text-sm font-medium text-gray-12"
            render={
              <Link href={routes.account.overview({ workspaceSlug: workspace.slug })}>
                <IconUser className="size-4 shrink-0 text-gray-11" />
                Account settings
              </Link>
            }
          />
        </DropdownMenuGroup>
        <DropdownMenuSeparator className="mx-0" />
        <DropdownMenuGroup className="p-1">
          <DropdownMenuLabel className="px-2">Theme</DropdownMenuLabel>
          <DropdownMenuRadioGroup
            aria-label="Theme"
            value={theme ?? "system"}
            onValueChange={setTheme}
          >
            {THEMES.map(({ value, label, icon: Icon }) => (
              <DropdownMenuRadioItem
                key={value}
                value={value}
                className="h-8 cursor-pointer px-2 text-sm font-medium text-gray-12"
              >
                <Icon className="size-4 shrink-0 text-gray-11" />
                {label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
        <DropdownMenuSeparator className="mx-0" />
        <DropdownMenuGroup className="p-1">
          <DropdownMenuItem
            className="h-8 cursor-pointer gap-2 px-2 text-sm font-medium text-gray-12"
            onClick={async () => {
              queryClient.clear();
              await signOut();
            }}
          >
            Sign out
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
